import { z } from 'zod'
import { DateTimeStringSchema } from './common'

const CampaignLanguageSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string().describe('言語コード（例: ja / en）'),
  label: z.string().describe('表示ラベル'),
  displayNumber: z.number().describe('表示順'),
})

const CampaignMapCenterSchema = z.object({
  lat: z.number(),
  lng: z.number(),
})

const CampaignOfficialSnsSchema = z.object({
  type: z.string().describe('SNS種別（facebook / instagram / x / line 等）'),
  url: z.string().describe('公式SNSのURL'),
  displayOrder: z.number().describe('表示順'),
})

const CampaignHomePopupSchema = z.object({
  image: z.string().nullable().describe('画像URL（CloudFront）'),
  url: z.string().nullable().describe('クリック時遷移先URL'),
  displayOrder: z.number().describe('表示順'),
})

const CampaignIllustrationMapSchema = z.object({
  name: z.string().nullable().describe('画像マップ名'),
  url: z.string().describe('画像URL（CloudFront）'),
})

export const CampaignDataSchema = z.object({
  id: z.number().describe('キャンペーンID'),
  name: z.string().describe('キャンペーン名'),
  type: z.number().describe('キャンペーン種別（1: スタンプ / 2: くじ / 3: その他）'),
  description: z.string().nullable().describe('キャンペーン説明文'),
  image: z.string().nullable().describe('キャンペーントップ画像 CloudFront URL'),
  campaignStartAt: DateTimeStringSchema.nullable().describe('キャンペーン開始日時（ISO 8601）'),
  campaignEndAt: DateTimeStringSchema.nullable().describe('キャンペーン終了日時（ISO 8601）'),
  playingStartAt: DateTimeStringSchema.nullable().describe('スタンプ押印可能期間 開始日時（ISO 8601）'),
  playingEndAt: DateTimeStringSchema.nullable().describe('スタンプ押印可能期間 終了日時（ISO 8601）'),
  exchangeStartAt: DateTimeStringSchema.nullable().describe('景品交換可能期間 開始日時（ISO 8601）'),
  exchangeEndAt: DateTimeStringSchema.nullable().describe('景品交換可能期間 終了日時（ISO 8601）'),
  viewStartAt: DateTimeStringSchema.nullable().describe('キャンペーン表示開始日時（ISO 8601）'),
  viewEndAt: DateTimeStringSchema.nullable().describe('キャンペーン表示終了日時（ISO 8601）'),
  usageHtml: z.string().nullable().describe('利用規約HTML'),
  outOfTermMessageHtml: z.string().nullable().describe('期間後メッセージHTML'),
  beforeTermMessageHtml: z.string().nullable().default(null).describe('期間前メッセージHTML'),
  listType: z.number().nullable().describe('スタンプカード一覧の表示形式'),
  isRepeatStamp: z
    .boolean()
    .nullable()
    .describe('リピートスタンプ有効フラグ（true: コンプリート後もスタンプを繰り返し取得可能）'),
  isRepeatStampWeekly: z
    .boolean()
    .nullable()
    .describe('週次リピートスタンプ有効フラグ（true: 毎週リセットしてスタンプ取得可能）'),
  mapCaption: z.string().nullable().describe('マップキャプション'),
  isQRCodeWithGPS: z.boolean().nullable().describe('QRコード押印時にGPS照合を行うフラグ'),
  isKeywordWithGPS: z.boolean().nullable().describe('キーワード押印時にGPS照合を行うフラグ'),
  isBarCodeWithGPS: z.boolean().nullable().describe('バーコード押印時にGPS照合を行うフラグ'),
  nonStampImage: z.string().nullable().describe('スタンプ未取得時の画像 CloudFront URL'),
  isDisplayEventPeriod: z.boolean().nullable().describe('イベント期間を表示するフラグ'),
  lotteryImage: z.string().nullable().describe('くじ演出画像 CloudFront URL'),
  isLocalize: z.boolean().nullable().describe('多言語対応フラグ（true: 言語切替UI を表示）'),
  locale: z.string().nullable().describe('現在の表示ロケール（例: ja_JP / en_US）'),
  mainColor: z.string().nullable().describe('メインカラー（16進数カラーコード）'),
  subColor: z.string().nullable().describe('サブカラー（16進数カラーコード）'),
  mapPinImage: z.string().nullable().describe('マップピン画像 CloudFront URL'),
  backgroundImage: z.string().nullable().describe('背景画像 CloudFront URL'),
  homeExpansionTopImage: z.string().nullable().describe('現在有効なHOME上部挿入画像 CloudFront URL'),
  homeExpansionBottomImage: z.string().nullable().describe('現在有効なHOME下部挿入画像 CloudFront URL'),
  mapCenter: CampaignMapCenterSchema.nullable().describe('マップ中心座標'),
  sameDayReset: z
    .boolean()
    .describe('当日リセット有効フラグ（true: 1日1回スタンプをリセット。isReset=true の場合のみ有効）'),
  isReset: z.boolean().describe('リセット有効フラグ（true: スタンプのリセット機能が有効 / false: リセットなし）'),
  isRestore: z.boolean().describe('復元機能有効フラグ（true: 復元機能あり / false: 復元機能なし）'),
  stampShape: z.number().nullable().describe('スタンプの形状'),
  extensionMvUrl: z.string().nullable().describe('拡張MV動画URL'),
  custom: z.string().nullable().describe('カスタムフィールド（JSON文字列）'),
  language: z.array(CampaignLanguageSchema).describe('対応言語一覧'),
  viewLineFriend: z.boolean().nullable().describe('LINE友だち追加ボタンを表示するフラグ'),
  forcedLineFriend: z
    .boolean()
    .nullable()
    .describe('LINE友だち追加を強制するフラグ（true: 未友だちユーザーを友だち追加へ誘導）'),
  lineCallbackUrl: z.string().nullable().describe('LINE連携コールバックURL'),
  createdAt: DateTimeStringSchema.nullable().describe('レコード作成日時（ISO 8601）'),
  updatedAt: DateTimeStringSchema.nullable().describe('レコード更新日時（ISO 8601）'),
  officialSns: z.array(CampaignOfficialSnsSchema).describe('公式SNSリンク一覧（displayOrder 昇順）'),
  homePopup: z.array(CampaignHomePopupSchema).describe('ホーム画面ポップアップ画像一覧（現在有効・displayOrder 昇順）'),
  isEcEnabled: z.boolean().default(false).describe('EC機能有効フラグ'),
  tokushoho: z.string().nullable().default(null).describe('特定商取引法に基づく表記HTML'),
  isTestMode: z.boolean().default(false).describe('テストモードフラグ'),
  redirectUrl: z.string().nullable().default(null).describe('リダイレクト先URL'),
  illustrationMaps: z
    .array(CampaignIllustrationMapSchema)
    .default([])
    .describe('画像マップ一覧（地図設定 > 画像マップ）'),
  stampTypes: z
    .array(z.enum(['GPS', 'QR_CODE', 'KEYWORD', 'BARCODE', 'OCR']))
    .default([])
    .describe('このキャンペーンで使用されるスタンプ獲得方法の一覧（削除済みスタンプを除いたユニーク値）'),
  sheetTypes: z
    .array(z.enum(['stamp_rally', 'point_rally', 'point_card', 'overlay']))
    .default([])
    .describe('このキャンペーンに紐づく有効シートのシート種別ユニーク一覧'),
})

export const CampaignGetResponseSchema = z.object({
  campaign: CampaignDataSchema.nullable(),
})

export type CampaignLanguage = z.infer<typeof CampaignLanguageSchema>
export type CampaignMapCenter = z.infer<typeof CampaignMapCenterSchema>
export type CampaignOfficialSns = z.infer<typeof CampaignOfficialSnsSchema>
export type CampaignHomePopup = z.infer<typeof CampaignHomePopupSchema>
export type CampaignIllustrationMap = z.infer<typeof CampaignIllustrationMapSchema>
export type CampaignData = z.infer<typeof CampaignDataSchema>
export type CampaignGetResponse = z.infer<typeof CampaignGetResponseSchema>
